import {
    ProposalNodeSet,
    GlobalCosts,
    GlobalCostItem,
    InstallNodeData,
    ProposalDiscount,
    FreightNodeData,
    type DesignEstimatingData,
    type ProposalCostingModel,
} from '../types';
import type { ProposalRateSettings } from './appSettings';
import { sumInstallEquipmentRaw } from './proposalInstallEquipment';
import {
    effectiveGlobalCostsTotal,
    estimatingRaw,
    type DesignRateInputs,
} from './proposalDesignBreakdown';

/** First-enable / new-custom-proposal default for project overhead %. */
export const DEFAULT_PROJECT_OVERHEAD_PERCENT = 20;

const EMPTY_GLOBAL_COST_ITEM: GlobalCostItem = { enabled: false, amount: 0, notes: '' };

const EMPTY_FREIGHT: FreightNodeData = {
    milesToAspers: 0,
    milesToJobSite: 0,
    costPerMileToAspers: 0,
    costPerMileToJobSite: 0,
    flatFeeToAspers: 0,
    flatFeeToJobSite: 0,
    total: 0,
};

/** Dollar-line defaults with project overhead off — safe merge fallback for existing proposals. */
export function createEmptyGlobalCosts(): GlobalCosts {
    return {
        baselineTravel: { ...EMPTY_GLOBAL_COST_ITEM },
        additionalTravel: { ...EMPTY_GLOBAL_COST_ITEM },
        globalFreightInbound: { ...EMPTY_GLOBAL_COST_ITEM },
        globalFreightOutbound: { ...EMPTY_GLOBAL_COST_ITEM },
        equipmentRental: { ...EMPTY_GLOBAL_COST_ITEM },
        additionalGear: { ...EMPTY_GLOBAL_COST_ITEM },
        ppeBudget: { ...EMPTY_GLOBAL_COST_ITEM },
        jobsiteIncidentals: { ...EMPTY_GLOBAL_COST_ITEM },
        additionalInsurance: { ...EMPTY_GLOBAL_COST_ITEM },
        damagedTimberBudget: { ...EMPTY_GLOBAL_COST_ITEM },
        jobsiteSecurity: { ...EMPTY_GLOBAL_COST_ITEM },
        additionalVehicles: { ...EMPTY_GLOBAL_COST_ITEM },
        jobSpecificTooling: { ...EMPTY_GLOBAL_COST_ITEM },
        totalGlobalCosts: 0,
        projectOverheadPercentEnabled: false,
        projectOverheadPercent: 0,
    };
}

/** Seeded only on newly created custom proposals. */
export function createDefaultGlobalCostsForNewCustomProposal(): GlobalCosts {
    return {
        ...createEmptyGlobalCosts(),
        projectOverheadPercentEnabled: true,
        projectOverheadPercent: DEFAULT_PROJECT_OVERHEAD_PERCENT,
    };
}

/**
 * New proposals (blank create, kit wizard, take-off seed, etc.) roll design
 * into the timber package by default. Display/math for existing docs must use
 * {@link resolveDesignIncludedInKit} so a missing field stays off.
 */
export const DEFAULT_DESIGN_INCLUDED_IN_KIT_FOR_NEW = true as const;

/**
 * Stored value for public totals / QB / builder load.
 * Missing, undefined, or any non-true value → false (legacy proposals unchanged).
 */
export function resolveDesignIncludedInKit(stored: unknown): boolean {
    return stored === true;
}

/**
 * Value to persist when creating a new proposalBuilders doc.
 * Explicit true/false on a clone source is preserved; missing/undefined uses
 * the new-create default (so duplicate-from-legacy-template gets the new default).
 */
export function designIncludedInKitForNewProposalWrite(sourceStored?: unknown): boolean {
    if (sourceStored === true || sourceStored === false) return sourceStored;
    return DEFAULT_DESIGN_INCLUDED_IN_KIT_FOR_NEW;
}

export function usesGlobalStructureCosting(state?: {
    costingModel?: ProposalCostingModel | string | null;
    isKitProposal?: boolean;
} | null): boolean {
    return state?.costingModel === 'global' && !state?.isKitProposal;
}

/**
 * Defensive: global-model custom proposals must not carry leftover node freight/overhead
 * (duplicate / import). Returns the same array reference when already clean.
 */
export function zeroNodeFreightAndOverheadForGlobalCosting(
    nodeSets: ProposalNodeSet[]
): ProposalNodeSet[] {
    let changed = false;
    const result = nodeSets.map(ns => {
        const freight = ns.freight || EMPTY_FREIGHT;
        const overhead = ns.overhead || { days: 0, total: 0 };
        const freightDirty =
            freight.total !== 0 ||
            freight.milesToAspers !== 0 ||
            freight.milesToJobSite !== 0 ||
            freight.costPerMileToAspers !== 0 ||
            freight.costPerMileToJobSite !== 0 ||
            freight.flatFeeToAspers !== 0 ||
            freight.flatFeeToJobSite !== 0;
        const overheadDirty = (overhead.days || 0) !== 0 || (overhead.total || 0) !== 0;
        if (!freightDirty && !overheadDirty) return ns;
        changed = true;
        return {
            ...ns,
            freight: { ...EMPTY_FREIGHT },
            overhead: { days: 0, total: 0 },
        };
    });
    return changed ? result : nodeSets;
}

/** Effective flat discount dollars for a proposal (0 when disabled/invalid). */
export function getProposalDiscountAmount(discount?: ProposalDiscount | null): number {
    if (!discount?.enabled) return 0;
    const amount = Number(discount.amount);
    if (!Number.isFinite(amount) || amount <= 0) return 0;
    return amount;
}

/**
 * Apply profit margin to a cost amount
 * Uses TRUE margin formula: finalPrice = cost / (1 - margin/100)
 * Example: 55% margin means cost is 45% of final price
 * So $9,544 cost with 55% margin = $9,544 / 0.45 = $21,209 final price
 */
export const applyProfitMargin = (cost: number, marginPercent: number): number => {
    if (marginPercent >= 100) {
        // Prevent division by zero or negative
        return cost * 10; // Cap at 10x for safety
    }
    return cost / (1 - marginPercent / 100);
};

/** Per-proposal setting (Global Costs modal): % of (structure raw + global line items). */
export function getEffectiveProjectOverheadPercent(globalCosts?: GlobalCosts | null): number {
    if (!globalCosts?.projectOverheadPercentEnabled) return 0;
    const p = Number(globalCosts.projectOverheadPercent);
    if (!Number.isFinite(p) || p <= 0) return 0;
    return Math.min(100, p);
}

/**
 * Install node raw total (labor + equipment + install fuel). Matches StructureDetailModal / builder.
 * Labor role lines only affect this via `teamLaborPerDay` (their qty × $/day rollup).
 * When `excludeInstallDayOverheadRate` is true (project overhead % of total is on), install days
 * use team labor only — overheadDailyRate is not multiplied by install days (avoids double-count).
 * `fuelBudget` here is install-only and is not travel.fuelBudget.
 */
export function computeInstallNodeRawTotal(
    install: InstallNodeData,
    rates: ProposalRateSettings,
    options?: { excludeInstallDayOverheadRate?: boolean }
): number {
    const exclude = options?.excludeInstallDayOverheadRate ?? false;
    const dayRate = exclude
        ? install.teamLaborPerDay
        : install.teamLaborPerDay + rates.overheadDailyRate;
    const labor = install.days * dayRate;
    const fuel = Number(install.fuelBudget) || 0;
    return labor + sumInstallEquipmentRaw(install, rates) + fuel;
}

export interface CalculateTotalsOptions {
    /** Sum of enabled global project costs (from Global Costs modal). */
    globalCostsTotal?: number;
    /** Percent of (structure raw + global costs) added as extra project overhead. 0 = none. */
    overheadPercentOfProject?: number;
    /** Flat promotional discount subtracted from the post-margin billable total. */
    discountAmount?: number;
    /** Proposal-level pre-sign estimating / modeling (added once into global costs, not × qty). */
    designEstimating?: DesignEstimatingData | null;
    designEstimatingRates?: DesignRateInputs | null;
    proposalStatus?: string | null;
}

/**
 * Calculate totals for the entire proposal.
 * Optional global costs and project-overhead % apply after per-structure sums (same base as summary UI).
 */
export const calculateTotals = (
    nodeSets: ProposalNodeSet[],
    profitMargin: number,
    options?: CalculateTotalsOptions
) => {
    let designTotal = 0;
    let timberKitTotal = 0;
    let installTotal = 0;

    nodeSets.forEach(ns => {
        const qty = ns.quantity || 1;

        // Design
        designTotal += ns.design.total * qty;

        // Timber Kit (Production + Materials + Freight)
        timberKitTotal += ns.production.total * qty;
        timberKitTotal += ns.materials.total * qty;
        timberKitTotal += ns.freight.total * qty;

        // Install (Install + structure Overhead node + Travel + install-line overhead)
        installTotal += ns.install.total * qty;
        installTotal += ns.overhead.total * qty;
        installTotal += ns.travel.total * qty;
        installTotal += (ns.install.overheadTotal || 0) * qty;
    });

    const structureRawCostTotal = designTotal + timberKitTotal + installTotal;
    const globalCostsTotal = effectiveGlobalCostsTotal(
        options?.globalCostsTotal,
        options?.designEstimating,
        options?.designEstimatingRates,
        options?.proposalStatus,
    );
    const pct = Math.max(0, options?.overheadPercentOfProject ?? 0);
    const baseForProjectOverhead = structureRawCostTotal + globalCostsTotal;
    const projectOverheadAmount =
        pct > 0 ? Math.round(baseForProjectOverhead * (pct / 100)) : 0;
    const rawCostTotal = baseForProjectOverhead + projectOverheadAmount;
    // Promotional discount applies AFTER margin: it reduces the client price
    // dollar-for-dollar, so gross profit absorbs the full concession.
    const discountAmount = Math.max(0, options?.discountAmount ?? 0);
    const undiscountedBillable = applyProfitMargin(rawCostTotal, profitMargin);
    const billableTotal = Math.max(0, undiscountedBillable - discountAmount);
    const grossProfit = billableTotal - rawCostTotal;

    return {
        designTotal,
        timberKitTotal,
        installTotal,
        structureRawCostTotal,
        globalCostsTotal,
        projectOverheadAmount,
        rawCostTotal,
        discountAmount,
        billableTotal,
        grossProfit
    };
};

/**
 * Calculate totals for a specific node set
 */
export const calculateNodeSetTotal = (nodeSet: ProposalNodeSet) => {
    return (
        nodeSet.design.total +
        nodeSet.production.total +
        nodeSet.materials.total +
        nodeSet.freight.total +
        nodeSet.overhead.total +
        nodeSet.install.total +
        nodeSet.travel.total +
        (nodeSet.install.overheadTotal || 0)
    );
};

/** Same base as ProposalSummary / builder footer — % of (structure raw + global costs). */
function projectOverheadRaw(baseRaw: number, pct: number): number {
    const p = Math.max(0, pct || 0);
    if (p <= 0 || baseRaw <= 0) return 0;
    return Math.round(baseRaw * (p / 100));
}

export interface ProposalSendSummaryEconomics {
    rawCostTotal: number;
    billableTotal: number;
    grossProfit: number;
    globalCostTotal: number;
    projectOverheadAmount: number;
    structureRawCostTotal: number;
    /** Flat promotional discount already subtracted from billableTotal. */
    discountAmount: number;
}

/**
 * Full-project raw cost, billable, and profit — matches ProposalSummary / proposal builder footer
 * (structure costs + global costs + project overhead %, then margin).
 */
export function computeProposalSendSummaryEconomics(
    nodeSets: ProposalNodeSet[],
    profitMargin: number,
    globalCosts: GlobalCosts | null | undefined,
    options?: {
        isKitProposal?: boolean;
        kitPricing?: Array<{ speciesName: string; totalCost: number }>;
        activeSpecies?: string;
        /** Flat promotional discount subtracted from the post-margin billable total. */
        discountAmount?: number;
        designEstimating?: DesignEstimatingData | null;
        designEstimatingRates?: DesignRateInputs | null;
        proposalStatus?: string | null;
    }
): ProposalSendSummaryEconomics {
    const enabled = nodeSets.filter(ns => ns.enabled !== false);
    const globalCostTotal = Math.max(0, globalCosts?.totalGlobalCosts ?? 0);
    const overheadPercentOfProject = getEffectiveProjectOverheadPercent(globalCosts);
    const discountAmount = Math.max(0, options?.discountAmount ?? 0);

    if (options?.isKitProposal && options.kitPricing && options.kitPricing.length > 0 && options.activeSpecies) {
        const selectedPricing = options.kitPricing.find(kp => kp.speciesName === options.activeSpecies);
        if (selectedPricing) {
            let installRawTotal = 0;
            enabled.forEach(ns => {
                const qty = ns.quantity || 1;
                installRawTotal += (ns.install.total + ns.travel.total) * qty;
            });

            const kitBillable = selectedPricing.totalCost;
            const installBillable = applyProfitMargin(installRawTotal, profitMargin);

            let structureRawForOverhead = 0;
            enabled.forEach(ns => {
                const qty = ns.quantity || 1;
                structureRawForOverhead +=
                    (ns.design.total +
                        ns.production.total +
                        ns.materials.total +
                        ns.freight.total +
                        ns.overhead.total +
                        ns.install.total +
                        ns.travel.total +
                        (ns.install.overheadTotal || 0)) *
                    qty;
            });
            const estimating = estimatingRaw(
                options?.designEstimating,
                options?.designEstimatingRates,
                options?.proposalStatus,
            );
            const globalWithEstimating = globalCostTotal + estimating;
            const projectOverheadAmount = projectOverheadRaw(
                structureRawForOverhead + globalWithEstimating,
                overheadPercentOfProject
            );
            const rawCostTotal = kitBillable + installRawTotal + globalWithEstimating + projectOverheadAmount;
            const undiscountedBillable =
                kitBillable +
                installBillable +
                applyProfitMargin(globalWithEstimating, profitMargin) +
                applyProfitMargin(projectOverheadAmount, profitMargin);
            const billableTotal = Math.max(0, undiscountedBillable - discountAmount);
            const grossProfit = billableTotal - rawCostTotal;

            return {
                rawCostTotal,
                billableTotal,
                grossProfit,
                globalCostTotal: globalWithEstimating,
                projectOverheadAmount,
                structureRawCostTotal: structureRawForOverhead,
                discountAmount,
            };
        }
    }

    const t = calculateTotals(enabled, profitMargin, {
        globalCostsTotal: globalCostTotal,
        overheadPercentOfProject,
        discountAmount,
        designEstimating: options?.designEstimating,
        designEstimatingRates: options?.designEstimatingRates,
        proposalStatus: options?.proposalStatus,
    });
    return {
        rawCostTotal: t.rawCostTotal,
        billableTotal: t.billableTotal,
        grossProfit: t.grossProfit,
        globalCostTotal: t.globalCostsTotal,
        projectOverheadAmount: t.projectOverheadAmount,
        structureRawCostTotal: t.structureRawCostTotal,
        discountAmount: t.discountAmount,
    };
}

/** Enabled structures that participate in the global dollar cost split. */
export function countGlobalCostShareEligible(nodeSets: ProposalNodeSet[]): number {
    return nodeSets.filter(ns => ns.enabled !== false && !ns.excludeFromGlobalPrices).length;
}

/**
 * When project overhead (% of total) is enabled, per-structure overhead days must be zero —
 * project OH replaces shop/day overhead on each structure. Also recalculates install totals
 * (with/without overhead daily rate on install days). Returns the same array reference if unchanged.
 */
export function syncNodeSetsForProjectOverhead(
    nodeSets: ProposalNodeSet[],
    globalCosts: GlobalCosts | null | undefined,
    rates: ProposalRateSettings
): ProposalNodeSet[] {
    const projectOhOn = getEffectiveProjectOverheadPercent(globalCosts) > 0;
    let changed = false;

    const result = nodeSets.map(ns => {
        const installTotal = computeInstallNodeRawTotal(ns.install, rates, {
            excludeInstallDayOverheadRate: projectOhOn,
        });

        if (projectOhOn) {
            const overheadNeedsZero = ns.overhead.days !== 0 || ns.overhead.total !== 0;
            const installOhNeedsZero = (ns.install.overheadTotal || 0) !== 0;
            const installNeedsRecalc = Math.abs(installTotal - ns.install.total) >= 0.01;

            if (!overheadNeedsZero && !installOhNeedsZero && !installNeedsRecalc) {
                return ns;
            }

            changed = true;
            return {
                ...ns,
                overhead: { ...ns.overhead, days: 0, total: 0 },
                install: {
                    ...ns.install,
                    overheadTotal: 0,
                    total: installTotal,
                },
            };
        }

        if (Math.abs(installTotal - ns.install.total) >= 0.01) {
            changed = true;
            return {
                ...ns,
                install: { ...ns.install, total: installTotal },
            };
        }

        return ns;
    });

    return changed ? result : nodeSets;
}

/**
 * Raw (pre-margin) global project cost share for one structure — matches builder save logic.
 * Used when recomputing client PDF / portal without billablePrices.
 */
export function getGlobalCostAddonRawForNode(
    nodeSet: ProposalNodeSet,
    totalGlobalCosts: number,
    allNodeSets: ProposalNodeSet[],
    /** When set, global costs split only among this pool (e.g. client-selected structures). */
    eligiblePool?: ProposalNodeSet[],
): number {
    if (totalGlobalCosts <= 0) return 0;
    if (nodeSet.enabled === false) return 0;
    if (nodeSet.excludeFromGlobalPrices) return 0;
    const pool = eligiblePool ?? allNodeSets.filter(ns => ns.enabled !== false);
    if (!pool.some(ns => ns.id === nodeSet.id)) return 0;
    const eligibleCount = pool.filter(ns => !ns.excludeFromGlobalPrices).length;
    if (eligibleCount === 0) {
        return 0;
    }
    return totalGlobalCosts / eligibleCount;
}
